import { zonedDate } from '../utils/date-time';
import { ReactNode, useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import { Link, Navigate, useParams } from 'react-router-dom';
import { baseURL, urlFile } from '../AxiosConfig';
import { useAppContext } from '../contexts/AppContext';
import { PageGallery } from './PageGallery';
import { InstallApp, useAppInstallation } from './InstallApp';
import { highlightIcon } from './highlightIcons';
import { ServiceMenu } from './ServiceMenu';
import { PublicService, ServiceModal } from './ServiceModal';
import { Check, Clock, Contact, ExternalLink, Facebook, Frown, Instagram, LinkIcon, MapPin, Moon, Navigation, Phone, Quote, Sun } from 'lucide-react';
import { WhatsappIcon, XTwitterIcon } from '../components/BrandIcons';
import type { AppIcon } from '../components/BrandIcons';

type Service = { id: string; name: string; description?: string; duration?: number; price?: number; requiresSubscription?: boolean; imagesIds?: string[]; attendantsIds?: string[] };
type Plan = Service & { periodicity: string; quantity?: number; indeterminateQuantity?: boolean; singleValidityValue?: number; singleValidityUnit?: string; discount?: number; services: { name: string; quantity: number; recurrence: string }[] };
type Profile = { timezone?: string; estabId: string; name: string; imageId?: string; imagesIds?: string[]; headline?: string; subtitle?: string; about?: string; coverImageId?: string; aboutImageId?: string; highlights?: { icon?: string; title: string; text?: string }[]; testimonials?: { name: string; text: string }[]; phone?: string; whatsapp?: string; address?: string; addressUrl?: string; city?: string; state?: string; social: { name: string; url: string }[]; hours?: Record<string, { start: number; end: number }[]>; services: Service[]; plans: Plan[]; attendants?: { id: string; name: string; photoUrl?: string }[] };

const days = [['segunda', 'Segunda-feira'], ['terca', 'Terça-feira'], ['quarta', 'Quarta-feira'], ['quinta', 'Quinta-feira'], ['sexta', 'Sexta-feira'], ['sabado', 'Sábado'], ['domingo', 'Domingo']];
// Indice de Date.getDay() (0 = domingo) para a chave usada nos horarios.
const dayKeyByIndex = ['domingo', 'segunda', 'terca', 'quarta', 'quinta', 'sexta', 'sabado'];
const periods: Record<string, string> = { single: 'Pagamento único', weekly: 'Semanal', biweekly: 'Quinzenal', monthly: 'Mensal', bimonthly: 'Bimestral', quarterly: 'Trimestral', biannual: 'Semestral', yearly: 'Anual' };
const periodSuffix: Record<string, string> = { weekly: '/semana', biweekly: '/quinzena', monthly: '/mês', bimonthly: '/bimestre', quarterly: '/trimestre', biannual: '/semestre', yearly: '/ano' };
const recurrence: Record<string, string> = { semanal: 'por semana', mensal: 'por mês', total: 'no total' };
const socialIcons: Record<string, AppIcon> = { instagram: Instagram, facebook: Facebook, 'x / twitter': XTwitterIcon, twitter: XTwitterIcon };

const SocialIcon = ({ name }: { name: string }) => {
    const Icon = socialIcons[name.toLowerCase()] || LinkIcon;
    return <Icon className="mt-0.5 w-4 shrink-0 text-base-content/40" aria-hidden="true" />;
};

const HighlightIcon = ({ iconKey }: { iconKey?: string }) => {
    const Icon = highlightIcon(iconKey);
    return <Icon aria-hidden="true" />;
};

const money = (value?: number) => typeof value === 'number' && Number.isFinite(value) ? value.toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' }) : 'Consulte';
const hour = (minutes: number) => `${String(Math.floor(minutes / 60)).padStart(2, '0')}:${String(minutes % 60).padStart(2, '0')}`;
const duration = (minutes?: number) => {
    if (!minutes) return '';
    const h = Math.floor(minutes / 60);
    const m = minutes % 60;
    return h ? `${h}h${m ? ` ${m}min` : ''}` : `${m} min`;
};
const safeUrl = (value?: string) => {
    try { const url = new URL(value || ''); return ['https:', 'http:'].includes(url.protocol) ? url.href : undefined; } catch { return undefined; }
};
const fileUrl = (id: string) => `${urlFile}/${encodeURIComponent(id)}`;
const initials = (name: string) => name.trim().split(/\s+/).slice(0, 2).map((part) => part[0]).join('').toUpperCase();

type OpenStatus = { open: boolean; label: string } | null;

const getOpenStatus = (hours: Profile['hours'], timezone?: string, instant = Date.now()): OpenStatus => {
    if (!hours || !Object.keys(hours).length) return null;
    const now = zonedDate(instant, timezone);
    const minutes = now.hour() * 60 + now.minute();
    const today = hours[dayKeyByIndex[now.day()]] || [];
    const current = today.find((slot) => minutes >= slot.start && minutes < slot.end);
    if (current) return { open: true, label: `Aberto agora · fecha às ${hour(current.end)}` };
    const next = today.find((slot) => slot.start > minutes);
    if (next) return { open: false, label: `Fechado · abre às ${hour(next.start)}` };
    for (let offset = 1; offset <= 7; offset += 1) {
        const index = (now.day() + offset) % 7;
        const slots = hours[dayKeyByIndex[index]] || [];
        if (slots.length) {
            const dayLabel = offset === 1 ? 'amanhã' : days.find(([key]) => key === dayKeyByIndex[index])?.[1].toLowerCase();
            return { open: false, label: `Fechado · abre ${dayLabel} às ${hour(slots[0].start)}` };
        }
    }
    return { open: false, label: 'Fechado' };
};

const Section = ({ id, eyebrow, title, subtitle, center = false, children }: { id: string; eyebrow?: string; title: string; subtitle?: string; center?: boolean; children: ReactNode }) => (
    <section id={id} className="scroll-mt-24">
        <div className={`mb-6 sm:mb-8 ${center ? 'mx-auto max-w-xl text-center' : 'max-w-xl'}`}>
            {eyebrow && <div className="mb-2 text-xs font-semibold uppercase tracking-[0.14em] text-primary">{eyebrow}</div>}
            <h2 className="text-[1.75rem] font-semibold leading-tight tracking-tight sm:text-4xl">{title}</h2>
            {subtitle && <p className="mt-2 text-sm text-base-content/60 sm:text-base">{subtitle}</p>}
        </div>
        {children}
    </section>
);

// Listas de cards: no celular viram carrossel com snap, para nao alongar a pagina; o card seguinte fica aparente.
const cardRow = '-mx-5 flex snap-x snap-mandatory scroll-px-5 gap-3 overflow-x-auto px-5 [scrollbar-width:none] [&::-webkit-scrollbar]:hidden sm:mx-auto sm:snap-none sm:flex-wrap sm:justify-center sm:gap-4 sm:overflow-visible sm:px-0';
const cardItem = (count: number) =>
    `shrink-0 snap-start ${count === 1 ? 'w-full sm:max-w-sm' : 'w-[85%] sm:w-[calc(50%-0.5rem)]'} ${count > 2 && count !== 4 ? 'lg:w-[calc((100%-2rem)/3)]' : ''}`;

const Card = ({ children, className = '' }: { children: ReactNode; className?: string }) => (
    <div className={`rounded-2xl bg-base-100 ring-1 ring-base-content/[0.06] ${className}`}>{children}</div>
);

export default function EstablishmentPage() {
    const { slug } = useParams();
    const installation = useAppInstallation();
    const { theme, toggleTheme } = useAppContext();
    const [data, setData] = useState<Profile | null>(null);
    const [status, setStatus] = useState('loading');
    const [retry, setRetry] = useState(0);
    const [logoFailed, setLogoFailed] = useState(false);
    const [coverFailed, setCoverFailed] = useState(false);
    const [aboutFailed, setAboutFailed] = useState(false);
    const [openService, setOpenService] = useState<PublicService | null>(null);

    useEffect(() => {
        const controller = new AbortController();
        setData(null);
        setStatus('loading');
        axios.get<Profile>(`${baseURL}/landing-page/profile`, { params: { slug }, signal: controller.signal })
            .then(({ data }) => { if (!controller.signal.aborted) { setData(data); setStatus('ready'); } })
            .catch((error) => { if (!controller.signal.aborted) setStatus(error.response?.status === 404 ? 'missing' : 'error'); });
        return () => controller.abort();
    }, [slug, retry]);

    useEffect(() => {
        if (!data) return;
        const previous = document.title;
        document.title = `${data.name} | SalonFlow`;
        return () => { document.title = previous; };
    }, [data]);

    const [clock, setClock] = useState(() => Date.now());
    useEffect(() => {
        const interval = window.setInterval(() => setClock(Date.now()), 60000);
        return () => window.clearInterval(interval);
    }, []);
    const openStatus = useMemo(() => getOpenStatus(data?.hours, data?.timezone, clock), [data?.hours, data?.timezone, clock]);

    const loginUrl = data?.estabId ? `/login?estabId=${encodeURIComponent(data.estabId)}&page=${encodeURIComponent(slug || '')}` : '/login';
    if (status === 'missing') return <Navigate to={loginUrl} replace />;
    if (status === 'loading') {
        return (
            <div className="grid min-h-screen place-items-center bg-base-200" role="status">
                <span className="loading loading-spinner loading-md text-base-content/40" aria-label="Carregando estabelecimento"></span>
            </div>
        );
    }
    if (!data) {
        return (
            <div className="flex min-h-screen flex-col items-center justify-center gap-4 bg-base-200 p-6 text-center">
                <Frown className="text-3xl text-base-content/30" aria-hidden="true" />
                <p role="alert" className="text-base-content/70">Não foi possível carregar a página.</p>
                <button className="btn btn-primary rounded-full px-6" onClick={() => setRetry((value) => value + 1)}>Tentar novamente</button>
                <Link to={loginUrl} className="text-sm text-primary hover:underline">Ir para o login</Link>
            </div>
        );
    }

    const images = (data.imagesIds || []).map(fileUrl);
    const coverId = data.coverImageId || data.imagesIds?.[0];
    const cover = coverId && !coverFailed ? fileUrl(coverId) : undefined;
    const aboutId = data.aboutImageId || data.imagesIds?.find((id) => id !== coverId);
    const aboutImage = aboutId && !aboutFailed ? fileUrl(aboutId) : undefined;
    // Cadastros antigos costumam repetir o nome do estabelecimento na frase de destaque.
    const headline = data.headline?.trim().toLowerCase() === data.name.trim().toLowerCase() ? '' : data.headline?.trim();
    const highlights = data.highlights || [];
    const plans = [...data.plans].sort((a, b) => (a.price ?? Infinity) - (b.price ?? Infinity));
    const testimonials = data.testimonials || [];
    const mapUrl = safeUrl(data.addressUrl) || (data.address ? `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(data.address)}` : undefined);
    const whatsappUrl = data.whatsapp ? `https://wa.me/${data.whatsapp}` : undefined;
    const phoneHref = data.phone ? `tel:${data.phone.replace(/[^+\d]/g, '')}` : undefined;
    const logo = data.imageId && !logoFailed ? fileUrl(data.imageId) : undefined;
    const social = data.social.map((item) => ({ ...item, url: safeUrl(item.url) })).filter((item) => item.url);
    const todayKey = dayKeyByIndex[zonedDate(clock, data.timezone).day()];
    const hasHours = !!data.hours && Object.keys(data.hours).length > 0;
    const city = [data.city, data.state].filter(Boolean).join(' · ');
    const nav = [
        data.about && ['sobre', 'Sobre'],
        data.services.length && ['servicos', 'Serviços'],
        data.plans.length && ['planos', 'Planos'],
        data.attendants?.length && ['equipe', 'Equipe'],
        images.length && ['galeria', 'Galeria'],
        testimonials.length && ['depoimentos', 'Depoimentos'],
        ['informacoes', 'Contato'],
    ].filter(Boolean) as string[][];
    const hasMobileActions = !!(whatsappUrl || mapUrl || phoneHref);
    const attendantsById = new Map((data.attendants || []).map((attendant) => [attendant.id, { ...attendant, photoUrl: safeUrl(attendant.photoUrl) }]));
    const publicServices: PublicService[] = data.services.map((service) => ({
        ...service,
        images: (service.imagesIds || []).map(fileUrl),
        // Sem profissionais vinculados, o servico e atendido por toda a equipe (mesma regra da agenda).
        attendants: service.attendantsIds?.length
            ? service.attendantsIds.flatMap((id) => {
                  const attendant = attendantsById.get(id);
                  return attendant ? [attendant] : [];
              })
            : Array.from(attendantsById.values()),
    }));

    const LogoMark = ({ size }: { size: 'sm' | 'lg' }) => (
        <div className={`flex shrink-0 items-center justify-center ${size === 'lg' ? 'h-24 sm:h-32' : 'h-11 min-w-10'}`}>
            {logo ? (
                <img src={logo} alt={size === 'lg' ? `Logo de ${data.name}` : ''} className={`h-full w-auto object-contain ${size === 'lg' ? 'max-w-[16rem]' : 'max-w-[7rem]'}`} onError={() => setLogoFailed(true)} />
            ) : (
                <span className={`font-semibold text-base-content/60 ${size === 'lg' ? 'text-2xl' : 'text-xs'}`}>{initials(data.name)}</span>
            )}
        </div>
    );

    const onPhoto = !!cover;
    const intro = data.subtitle?.trim() || (headline ? '' : 'Conheça nossos serviços, veja o ambiente e agende seu horário.');

    return (
        <div className={`min-h-screen bg-base-200 text-base-content ${hasMobileActions ? 'pb-24 sm:pb-0' : ''}`}>
            <header className="sticky top-0 z-40 border-b border-base-content/[0.06] bg-base-200/75 backdrop-blur-xl backdrop-saturate-150">
                <div className="mx-auto flex h-16 max-w-6xl items-center justify-between gap-4 px-5">
                    <a href="#inicio" className="flex min-w-0 items-center gap-2.5">
                        <LogoMark size="sm" />
                        <span className="truncate text-sm font-semibold">{data.name}</span>
                    </a>
                    <nav aria-label="Seções da página" className="hidden items-center gap-6 text-sm text-base-content/65 lg:flex">
                        {nav.map(([id, label]) => (
                            <a key={id} href={`#${id}`} className="transition-colors hover:text-base-content">{label}</a>
                        ))}
                    </nav>
                    <div className="flex shrink-0 items-center gap-2">
                        <button
                            type="button"
                            onClick={toggleTheme}
                            aria-label={theme === 'dark' ? 'Usar tema claro' : 'Usar tema escuro'}
                            title={theme === 'dark' ? 'Tema claro' : 'Tema escuro'}
                            className="flex h-8 w-8 items-center justify-center rounded-full text-sm text-base-content/65 transition-colors hover:bg-base-content/[0.07] hover:text-base-content"
                        >
                            {theme === 'dark' ? <Sun aria-hidden="true" /> : <Moon aria-hidden="true" />}
                        </button>
                        <Link to={loginUrl} className="btn btn-primary btn-sm h-8 min-h-8 rounded-full px-4">Agendar</Link>
                    </div>
                </div>
            </header>

            <section
                id="inicio"
                className={`relative isolate scroll-mt-24 overflow-hidden ${onPhoto ? 'bg-[#101012] text-white' : 'border-b border-base-content/[0.06]'}`}
            >
                {cover && (
                    <>
                        <img src={cover} alt="" className="absolute inset-0 -z-10 h-full w-full object-cover" onError={() => setCoverFailed(true)} />
                        {/* Escurecimento em duas direcoes: o texto precisa ser legivel sobre qualquer foto. */}
                        <div className="absolute inset-0 -z-10 bg-gradient-to-t from-black/90 via-black/55 to-black/25 lg:bg-gradient-to-r lg:from-black/90 lg:via-black/70 lg:to-black/10" aria-hidden></div>
                    </>
                )}
                <div
                    className={`mx-auto flex max-w-6xl flex-col px-5 ${
                        onPhoto
                            ? 'min-h-[min(74svh,40rem)] justify-end pb-10 pt-24 sm:pb-14 lg:min-h-[min(82vh,46rem)] lg:justify-center lg:pb-20 lg:pt-20'
                            : 'items-center py-14 text-center sm:py-20'
                    }`}
                >
                    {!onPhoto && <div className="mb-6"><LogoMark size="lg" /></div>}
                    {(openStatus || city) && (
                        <div className={`flex flex-wrap items-center gap-x-3 gap-y-1.5 text-sm ${onPhoto ? 'text-white/80' : 'justify-center text-base-content/60'}`}>
                            {openStatus && (
                                <span className={`inline-flex items-center gap-1.5 font-medium ${openStatus.open ? (onPhoto ? 'text-[#30D158]' : 'text-success') : ''}`}>
                                    <span className={`h-2 w-2 rounded-full ${openStatus.open ? (onPhoto ? 'bg-[#30D158]' : 'bg-success') : onPhoto ? 'bg-white/50' : 'bg-base-content/30'}`}></span>
                                    {openStatus.label}
                                </span>
                            )}
                            {city && <span className="inline-flex items-center gap-1.5"><MapPin className="text-xs opacity-70" aria-hidden />{city}</span>}
                        </div>
                    )}
                    <h1 className="mt-3 max-w-3xl break-words text-[2.75rem] font-bold leading-[1.05] tracking-tight sm:text-6xl lg:text-7xl">{data.name}</h1>
                    {headline && <p className={`mt-4 max-w-2xl text-xl font-medium leading-snug sm:text-2xl ${onPhoto ? 'text-white' : ''}`}>{headline}</p>}
                    {intro && <p className={`mt-3 max-w-xl text-[0.9375rem] leading-relaxed sm:text-lg ${onPhoto ? 'text-white/75' : 'text-base-content/65'}`}>{intro}</p>}
                    {/* No celular a barra fixa ja oferece estas acoes. */}
                    <div className={`mt-7 flex-wrap gap-3 ${hasMobileActions ? 'hidden sm:flex' : 'flex'} ${onPhoto ? '' : 'justify-center'}`}>
                        <Link to={loginUrl} className="btn btn-primary h-12 rounded-full px-7 text-[0.9375rem]">Agendar horário</Link>
                        {whatsappUrl && (
                            <a
                                className={`btn h-12 rounded-full border-0 px-6 text-[0.9375rem] ${onPhoto ? 'bg-white/15 text-white backdrop-blur-md hover:bg-white/25' : 'bg-base-100 ring-1 ring-base-content/10 hover:bg-base-100/70'}`}
                                href={whatsappUrl}
                                target="_blank"
                                rel="noopener noreferrer"
                            >
                                <WhatsappIcon className="text-lg text-[#25D366]" aria-hidden="true" />
                                WhatsApp
                            </a>
                        )}
                        {mapUrl && (
                            <a
                                className={`btn btn-ghost h-12 rounded-full px-5 text-[0.9375rem] ${onPhoto ? 'text-white hover:bg-white/15' : 'text-primary'}`}
                                href={mapUrl}
                                target="_blank"
                                rel="noopener noreferrer"
                            >
                                Como chegar <ExternalLink className="text-xs" aria-hidden="true" />
                            </a>
                        )}
                    </div>
                </div>
            </section>

            {/* A navegacao do cabecalho so aparece no desktop; no celular, atalhos logo apos a capa. */}
            {nav.length > 1 && (
                <nav aria-label="Atalhos da página" className="flex gap-2 overflow-x-auto px-5 pt-4 [scrollbar-width:none] sm:justify-center lg:hidden [&::-webkit-scrollbar]:hidden">
                    {nav.map(([id, label]) => (
                        <a key={id} href={`#${id}`} className="shrink-0 rounded-full bg-base-100 px-4 py-2 text-sm font-medium text-base-content/80 ring-1 ring-base-content/[0.06]">{label}</a>
                    ))}
                </nav>
            )}

            <main className="mx-auto max-w-6xl space-y-16 px-5 pb-16 pt-10 sm:space-y-24 sm:pt-14">
                {!!highlights.length && (
                    <ul className="flex flex-wrap justify-center gap-x-5 gap-y-8 text-center">
                        {highlights.map((item, index) => (
                            <li key={index} className={`w-[calc(50%-0.625rem)] ${highlights.length === 3 ? 'sm:w-[calc((100%-2.5rem)/3)]' : highlights.length === 4 ? 'lg:w-[calc((100%-3.75rem)/4)]' : 'sm:w-64'}`}>
                                <span className="mx-auto flex h-11 w-11 items-center justify-center rounded-full bg-base-100 text-primary ring-1 ring-base-content/[0.06]" aria-hidden>
                                    <HighlightIcon iconKey={item.icon} />
                                </span>
                                <h2 className="mt-3 font-semibold leading-snug">{item.title}</h2>
                                {item.text && <p className="mt-1 text-sm leading-relaxed text-base-content/60">{item.text}</p>}
                            </li>
                        ))}
                    </ul>
                )}

                {!!data.about?.trim() && (
                    <section id="sobre" className={`scroll-mt-24 ${aboutImage ? 'grid items-center gap-8 lg:grid-cols-2 lg:gap-16' : 'mx-auto max-w-2xl text-center'}`}>
                        {aboutImage && (
                            // Proporcao natural: a foto pode ser de qualquer formato, e recortar estragaria logos e retratos.
                            <img src={aboutImage} alt={data.name} loading="lazy" onError={() => setAboutFailed(true)} className="max-h-[34rem] w-full rounded-3xl object-cover" />
                        )}
                        <div>
                            <div className="mb-2 text-xs font-semibold uppercase tracking-[0.14em] text-primary">Sobre</div>
                            <h2 className="text-[1.75rem] font-semibold leading-tight tracking-tight sm:text-4xl">Nossa história</h2>
                            <p className="mt-5 whitespace-pre-line text-[0.9375rem] leading-relaxed text-base-content/70 sm:text-lg">{data.about.trim()}</p>
                        </div>
                    </section>
                )}

                {!!publicServices.length && (
                    <Section id="servicos" eyebrow="Serviços" title="Serviços e valores" subtitle="Toque em um serviço para ver os detalhes.">
                        <ServiceMenu services={publicServices} money={money} duration={duration} onOpen={setOpenService} />
                    </Section>
                )}

                {!!plans.length && (
                    <Section id="planos" eyebrow="Planos" title="Economize com pacotes" subtitle="Assinaturas e pacotes para quem é cliente frequente." center>
                        <div className={`${cardRow} ${plans.length === 4 ? 'sm:max-w-4xl' : ''}`}>
                            {plans.map((plan) => (
                                <Card key={plan.id} className={`flex flex-col p-6 ${cardItem(plans.length)}`}>
                                    <div className="text-xs font-semibold uppercase tracking-wider text-primary">{periods[plan.periodicity] || 'Plano'}</div>
                                    <h3 className="mt-2 text-lg font-semibold">{plan.name}</h3>
                                    <p className="mt-3 text-3xl font-bold tracking-tight tabular-nums">
                                        {money(plan.price)}
                                        {typeof plan.price === 'number' && periodSuffix[plan.periodicity] && <span className="ml-1 text-sm font-medium tracking-normal text-base-content/55">{periodSuffix[plan.periodicity]}</span>}
                                    </p>
                                    {plan.description && <p className="mt-3 whitespace-pre-line text-sm text-base-content/65">{plan.description}</p>}
                                    {!!plan.services.length && (
                                        <ul className="mt-5 space-y-2 border-t border-base-content/[0.06] pt-5 text-sm">
                                            {plan.services.map((service, i) => (
                                                <li key={i} className="flex gap-2.5">
                                                    <Check className="mt-1 text-xs text-success" aria-hidden="true" />
                                                    <span>{service.quantity}× {service.name} <span className="text-base-content/55">{recurrence[service.recurrence] || ''}</span></span>
                                                </li>
                                            ))}
                                        </ul>
                                    )}
                                    <div className="mt-4 space-y-1 text-xs text-base-content/55">
                                        {plan.periodicity !== 'single' && !plan.indeterminateQuantity && !!plan.quantity && <p>{plan.quantity} cobranças</p>}
                                        {!!plan.singleValidityValue && <p>Validade: {plan.singleValidityValue} {plan.singleValidityUnit === 'month' ? 'meses' : 'dias'}</p>}
                                        {!!plan.discount && <p>{plan.discount}% de desconto nos serviços cobertos</p>}
                                    </div>
                                    <div className="mt-auto pt-6">
                                        <Link to={loginUrl} className="btn h-11 w-full rounded-full border-0 bg-base-200 text-sm hover:bg-base-300">
                                            Contratar plano
                                        </Link>
                                    </div>
                                </Card>
                            ))}
                        </div>
                    </Section>
                )}

                {!!data.attendants?.length && (
                    <Section id="equipe" eyebrow="Equipe" title="Nossos profissionais" subtitle="Quem cuida do seu visual." center>
                        <ul className="flex flex-wrap justify-center gap-x-4 gap-y-7 sm:gap-x-6">
                            {data.attendants.map((attendant) => {
                                const photo = safeUrl(attendant.photoUrl);
                                return (
                                    <li key={attendant.id} className="w-[calc(50%-0.5rem)] sm:w-56">
                                        <div className="relative flex aspect-[4/5] items-center justify-center overflow-hidden rounded-2xl bg-base-100 ring-1 ring-base-content/[0.06]" aria-hidden="true">
                                            <span className="text-3xl font-semibold text-base-content/30">{initials(attendant.name)}</span>
                                            {photo && <img src={photo} alt="" loading="lazy" className="absolute inset-0 h-full w-full object-cover" onError={(event) => { event.currentTarget.hidden = true; }} />}
                                        </div>
                                        <h3 className="mt-3 break-words text-center font-semibold">{attendant.name}</h3>
                                    </li>
                                );
                            })}
                        </ul>
                    </Section>
                )}

                {!!images.length && (
                    <Section id="galeria" eyebrow="Galeria" title="Nosso espaço e trabalho" subtitle="Um pouco do que você encontra por aqui.">
                        <PageGallery images={images} name={data.name} />
                    </Section>
                )}

                {!!testimonials.length && (
                    <Section id="depoimentos" eyebrow="Depoimentos" title="Quem vem, recomenda" center>
                        <ul className={`${cardRow} ${testimonials.length === 4 ? 'sm:max-w-4xl' : ''}`}>
                            {testimonials.map((item, index) => (
                                <li key={index} className={cardItem(testimonials.length)}>
                                    <Card className="flex h-full flex-col p-6">
                                        <Quote className="text-lg text-primary/50" aria-hidden />
                                        <blockquote className="mt-3 flex-1 whitespace-pre-line text-[0.9375rem] leading-relaxed text-base-content/80">{item.text}</blockquote>
                                        <p className="mt-4 text-sm font-semibold">{item.name}</p>
                                    </Card>
                                </li>
                            ))}
                        </ul>
                    </Section>
                )}

                <Section id="informacoes" eyebrow="Visite-nos" title="Horários e contato">
                    <div className="grid gap-4 lg:grid-cols-2">
                        <Card className="p-5 sm:p-6">
                            <h3 className="flex items-center gap-2 text-sm font-semibold"><Clock className="text-base-content/45" aria-hidden="true" />Horário de atendimento</h3>
                            {hasHours ? (
                                <dl className="mt-4 divide-y divide-base-content/[0.06] text-sm">
                                    {days.map(([key, label]) => {
                                        const isToday = key === todayKey;
                                        const slots = data.hours?.[key] || [];
                                        return (
                                            <div key={key} className={`flex items-center justify-between gap-4 py-2.5 ${isToday ? 'font-semibold' : ''}`}>
                                                <dt className="flex items-center gap-2">
                                                    {label}
                                                    {isToday && <span className="rounded-full bg-primary/10 px-2 py-0.5 text-[0.6875rem] font-semibold text-primary">Hoje</span>}
                                                </dt>
                                                <dd className={`text-right tabular-nums ${slots.length ? (isToday ? '' : 'text-base-content/70') : 'text-base-content/40'}`}>
                                                    {slots.length ? slots.map((slot) => `${hour(slot.start)} – ${hour(slot.end)}`).join(' · ') : 'Fechado'}
                                                </dd>
                                            </div>
                                        );
                                    })}
                                </dl>
                            ) : (
                                <p className="mt-4 text-sm text-base-content/60">Consulte os horários com o estabelecimento.</p>
                            )}
                            <p className="mt-4 text-xs text-base-content/45">Feriados e datas especiais podem ter horários diferentes.</p>
                        </Card>

                        <Card className="flex flex-col p-5 sm:p-6">
                            <h3 className="flex items-center gap-2 text-sm font-semibold"><Contact className="text-base-content/45" aria-hidden="true" />Contato e localização</h3>
                            <ul className="mt-4 divide-y divide-base-content/[0.06] text-sm">
                                {data.address && (
                                    <li className="flex gap-3 py-3">
                                        <MapPin className="mt-0.5 w-4 text-base-content/40" aria-hidden="true" />
                                        <address className="not-italic leading-relaxed">{data.address}</address>
                                    </li>
                                )}
                                {data.phone && phoneHref && (
                                    <li className="flex gap-3 py-3">
                                        <Phone className="mt-0.5 w-4 text-base-content/40" aria-hidden="true" />
                                        <a className="text-primary hover:underline" href={phoneHref}>{data.phone}</a>
                                    </li>
                                )}
                                {whatsappUrl && (
                                    <li className="flex gap-3 py-3">
                                        <WhatsappIcon className="mt-0.5 w-4 text-base-content/40" aria-hidden="true" />
                                        <a className="text-primary hover:underline" href={whatsappUrl} target="_blank" rel="noopener noreferrer">Conversar no WhatsApp</a>
                                    </li>
                                )}
                                {social.map((item) => (
                                    <li key={item.name} className="flex gap-3 py-3">
                                        <SocialIcon name={item.name} />
                                        <a className="text-primary hover:underline" href={item.url} target="_blank" rel="noopener noreferrer">{item.name}</a>
                                    </li>
                                ))}
                            </ul>
                            {!data.phone && !data.address && !social.length && <p className="text-sm text-base-content/60">Contato não informado.</p>}

                            {mapUrl && (
                                <div className="mt-auto pt-5">
                                    <a className="btn h-11 min-h-11 w-full rounded-full border-0 bg-base-200 text-sm hover:bg-base-300" href={mapUrl} target="_blank" rel="noopener noreferrer">
                                        <Navigation aria-hidden="true" />
                                        Como chegar
                                    </a>
                                </div>
                            )}
                        </Card>
                    </div>
                </Section>

                {/* No celular a barra fixa ja cumpre este papel. */}
                <section className={`rounded-3xl bg-[#1C1C1E] px-6 py-12 text-center text-white ring-1 ring-white/[0.06] sm:py-16 ${hasMobileActions ? 'hidden sm:block' : ''}`}>
                    <h2 className="text-[1.75rem] font-semibold leading-tight tracking-tight sm:text-4xl">Reserve seu horário</h2>
                    <p className="mt-2 text-white/65">Escolha o serviço, o profissional e o melhor horário para você.</p>
                    <Link to={loginUrl} className="btn btn-primary mt-7 h-12 rounded-full px-8 text-[0.9375rem]">Agendar agora</Link>
                </section>
            </main>

            <footer className="border-t border-base-content/[0.06]">
                <div className="mx-auto max-w-6xl px-5">
                    <InstallApp installation={installation} />
                </div>
                <div className="mx-auto flex max-w-6xl flex-col items-center justify-between gap-2 px-5 py-6 text-xs text-base-content/45 sm:flex-row">
                    <span>© {new Date().getFullYear()} {data.name}</span>
                    <span>Agendamentos por SalonFlow</span>
                </div>
            </footer>

            {openService && (
                <ServiceModal loginUrl={loginUrl} service={openService} money={money} duration={duration} initials={initials} onClose={() => setOpenService(null)} />
            )}

            {/* Acoes fixas no mobile, ao alcance do polegar. */}
            {hasMobileActions && (
                <div className="fixed inset-x-0 bottom-0 z-40 border-t border-base-content/[0.06] bg-base-200/80 px-4 pb-[calc(0.75rem+env(safe-area-inset-bottom))] pt-3 backdrop-blur-xl backdrop-saturate-150 sm:hidden">
                    <div className="flex gap-2">
                        <Link to={loginUrl} className="btn btn-primary h-12 flex-1 rounded-full text-[0.9375rem]">Agendar</Link>
                        {whatsappUrl && (
                            <a href={whatsappUrl} target="_blank" rel="noopener noreferrer" aria-label="WhatsApp" className="btn h-12 w-12 rounded-full border-0 bg-base-100 p-0 ring-1 ring-base-content/10">
                                <WhatsappIcon className="text-xl text-[#25D366]" aria-hidden="true" />
                            </a>
                        )}
                        {!whatsappUrl && phoneHref && (
                            <a href={phoneHref} aria-label="Ligar" className="btn h-12 w-12 rounded-full border-0 bg-base-100 p-0 ring-1 ring-base-content/10">
                                <Phone aria-hidden="true" />
                            </a>
                        )}
                        {mapUrl && (
                            <a href={mapUrl} target="_blank" rel="noopener noreferrer" aria-label="Como chegar" className="btn h-12 w-12 rounded-full border-0 bg-base-100 p-0 ring-1 ring-base-content/10">
                                <Navigation aria-hidden="true" />
                            </a>
                        )}
                    </div>
                </div>
            )}
        </div>
    );
}
